import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Grid,
  GridColumn,
  GridCellProps,
} from "@progress/kendo-react-grid";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { apiClient } from "src/dm/utils/axios";

interface DealMapping {
  categoryManagerCode: number;
  ownerUserId: string | null;
  supportTeamMember: string | null;
  scanbackEntryType: string | null;
  performanceFeeEntryType: string | null;
  adminFeeEntryType: string | null;
  rewardEntryType: string | null;
  businessUnit: string | null;
}

interface ListResponse {
  items: DealMapping[];
  total: number;
}

const baseUrl = "/dm/V1/admin/deal-mappings";
const emptyDraft = (): DealMapping => ({
  categoryManagerCode: 0,
  ownerUserId: "",
  supportTeamMember: "",
  scanbackEntryType: "",
  performanceFeeEntryType: "",
  adminFeeEntryType: "",
  rewardEntryType: "",
  businessUnit: "",
});

const DealMappingsPage = () => {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState<DealMapping>(emptyDraft);
  const [editRow, setEditRow] = useState<DealMapping | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["deal-mappings"],
    queryFn: async (): Promise<DealMapping[]> => {
      const response = await apiClient.get<ListResponse>(baseUrl);
      return response.data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (row: DealMapping) => {
      const response = await apiClient.post<DealMapping>(baseUrl, row);
      return response.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-mappings"] }),
  });

  const update = useMutation({
    mutationFn: async (row: DealMapping) => {
      const response = await apiClient.put<DealMapping>(
        `${baseUrl}/${row.categoryManagerCode}`,
        row
      );
      return response.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-mappings"] }),
  });

  const remove = useMutation({
    mutationFn: async (catMgrCode: number) => {
      await apiClient.delete(`${baseUrl}/${catMgrCode}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-mappings"] }),
  });

  const ActionsCell = (props: GridCellProps) => {
    const row = props.dataItem as DealMapping;
    return (
      <td>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          onClick={() => setEditRow({ ...row })}
        >
          Edit
        </Button>
        <Button
          type="button"
          size="small"
          fillMode="flat"
          themeColor="error"
          disabled={remove.isPending}
          onClick={() => {
            if (
              confirm(`Delete deal mapping for cat-mgr ${row.categoryManagerCode}?`)
            ) {
              remove.mutate(row.categoryManagerCode);
            }
          }}
        >
          Delete
        </Button>
      </td>
    );
  };

  const renderEditor = (
    row: DealMapping,
    setRow: (r: DealMapping) => void,
    isNew: boolean
  ) => (
    <div
      style={{
        display: "grid",
        gap: "0.5rem 0.75rem",
        gridTemplateColumns: "1fr 1fr",
      }}
    >
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Category Manager Code
        </span>
        <NumericTextBox
          value={row.categoryManagerCode || null}
          disabled={!isNew}
          onChange={(e) =>
            setRow({ ...row, categoryManagerCode: Number(e.value ?? 0) })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Owner User ID
        </span>
        <Input
          value={row.ownerUserId ?? ""}
          onChange={(e) =>
            setRow({ ...row, ownerUserId: String(e.target.value ?? "") })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Support Team Member
        </span>
        <Input
          value={row.supportTeamMember ?? ""}
          onChange={(e) =>
            setRow({ ...row, supportTeamMember: String(e.target.value ?? "") })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Scanback Entry Type
        </span>
        <Input
          value={row.scanbackEntryType ?? ""}
          onChange={(e) =>
            setRow({ ...row, scanbackEntryType: String(e.target.value ?? "") })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Performance Fee Entry Type
        </span>
        <Input
          value={row.performanceFeeEntryType ?? ""}
          onChange={(e) =>
            setRow({
              ...row,
              performanceFeeEntryType: String(e.target.value ?? ""),
            })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Admin Fee Entry Type
        </span>
        <Input
          value={row.adminFeeEntryType ?? ""}
          onChange={(e) =>
            setRow({ ...row, adminFeeEntryType: String(e.target.value ?? "") })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Reward Entry Type
        </span>
        <Input
          value={row.rewardEntryType ?? ""}
          onChange={(e) =>
            setRow({ ...row, rewardEntryType: String(e.target.value ?? "") })
          }
        />
      </label>
      <label>
        <span style={{ fontSize: "0.8rem", color: "#475569" }}>
          Business Unit
        </span>
        <Input
          value={row.businessUnit ?? ""}
          onChange={(e) =>
            setRow({ ...row, businessUnit: String(e.target.value ?? "") })
          }
        />
      </label>
    </div>
  );

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <Link
        to="/dm/admin"
        style={{
          fontSize: "0.85rem",
          color: "#0a3d62",
          textDecoration: "none",
        }}
      >
        ← Admin
      </Link>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Deal Mappings</h1>
          <p style={{ color: "#475569", margin: "0.25rem 0 0" }}>
            Maps each category manager to a default deal owner, support
            member, entry-type flags, and business unit.
          </p>
        </div>
        <Button
          type="button"
          themeColor="primary"
          onClick={() => {
            setDraft(emptyDraft());
            setShowAdd(true);
          }}
        >
          Add row
        </Button>
      </div>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {isLoading && <p>Loading…</p>}
        {isError && (
          <p style={{ color: "#b91c1c" }}>
            {error instanceof Error ? error.message : "Failed to load"}
          </p>
        )}
        {data && data.length === 0 && (
          <p style={{ color: "#94a3b8" }}>No deal mappings yet.</p>
        )}
        {data && data.length > 0 && (
          <Grid data={data}>
            <GridColumn field="categoryManagerCode" title="Cat Mgr" width="90px" />
            <GridColumn field="ownerUserId" title="Owner" />
            <GridColumn field="supportTeamMember" title="Support" width="120px" />
            <GridColumn field="scanbackEntryType" title="Scanback" width="100px" />
            <GridColumn
              field="performanceFeeEntryType"
              title="Perf Fee"
              width="100px"
            />
            <GridColumn field="adminFeeEntryType" title="Admin Fee" width="100px" />
            <GridColumn field="rewardEntryType" title="Reward" width="100px" />
            <GridColumn field="businessUnit" title="Bus Unit" width="100px" />
            <GridColumn title="" width="160px" cells={{ data: ActionsCell }} />
          </Grid>
        )}
      </section>

      {showAdd && (
        <Dialog
          title="Add deal mapping"
          width={680}
          onClose={() => setShowAdd(false)}
        >
          {renderEditor(draft, setDraft, true)}
          {create.isError && (
            <pre style={{ color: "#b91c1c", fontSize: "0.8rem" }}>
              {create.error instanceof Error
                ? create.error.message
                : "Save failed"}
            </pre>
          )}
          <DialogActionsBar>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={create.isPending || !draft.categoryManagerCode}
              onClick={async () => {
                await create.mutateAsync(draft);
                setShowAdd(false);
              }}
            >
              {create.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {editRow && (
        <Dialog
          title={`Edit deal mapping for cat-mgr ${editRow.categoryManagerCode}`}
          width={680}
          onClose={() => setEditRow(null)}
        >
          {renderEditor(editRow, setEditRow, false)}
          {update.isError && (
            <pre style={{ color: "#b91c1c", fontSize: "0.8rem" }}>
              {update.error instanceof Error
                ? update.error.message
                : "Save failed"}
            </pre>
          )}
          <DialogActionsBar>
            <Button onClick={() => setEditRow(null)}>Cancel</Button>
            <Button
              themeColor="primary"
              disabled={update.isPending}
              onClick={async () => {
                await update.mutateAsync(editRow);
                setEditRow(null);
              }}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default DealMappingsPage;
